import { TimetableRelationshipProperties } from '@bp/planung-graphql-types';
import { makeObservable, observable, runInAction } from 'mobx';
import { initialMatrix } from '../components/TimetableVersion/utils/TimetableVersionTableUtils';
import { getCacheTag } from '../hooks/useMemorizedCacheTag';
import {
  UpdateTimetableVersionClassAvailabilityDocument,
  UpdateTimetableVersionClassAvailabilityMutation,
  UpdateTimetableVersionClassAvailabilityMutationVariables,
  UpdateTimetableVersionRoomAvailabilityDocument,
  UpdateTimetableVersionRoomAvailabilityMutation,
  UpdateTimetableVersionRoomAvailabilityMutationVariables,
  UpdateTimetableVersionSubjectAvailabilityDocument,
  UpdateTimetableVersionSubjectAvailabilityMutation,
  UpdateTimetableVersionSubjectAvailabilityMutationVariables,
  UpdateTimetableVersionTeachersAvailabilityDocument,
  UpdateTimetableVersionTeachersAvailabilityMutation,
  UpdateTimetableVersionTeachersAvailabilityMutationVariables,
} from '../types/planung-graphql-client-defs';
import { urqlClient } from '../utils/urqlClient';

export type Availabilities = {
  class: {
    uuid: string;
    availabilities: TimetableRelationshipProperties;
  }[];
  teacher: {
    uuid: string;
    availabilities: TimetableRelationshipProperties;
  }[];
  room: {
    uuid: string;
    availabilities: TimetableRelationshipProperties;
  }[];
  subject: {
    uuid: string;
    availabilities: TimetableRelationshipProperties;
  }[];
};

export class AvailabilityStore {
  public availabilities: Availabilities;

  constructor() {
    this.availabilities = {
      class: [],
      teacher: [],
      room: [],
      subject: [],
    };
    makeObservable(this, {
      availabilities: observable,
    });
  }

  public async setAvailability(
    type: keyof Availabilities,
    uuid: string,
    availability: TimetableRelationshipProperties,
    versionUuid?: string,
  ) {
    runInAction(() => {
      const currentTypeAvailabilities = this.availabilities[type];
      if (currentTypeAvailabilities.length === 0) {
        this.availabilities[type] = [
          {
            uuid,
            availabilities: availability,
          },
        ];
        return;
      } else {
        const currentAvailability = currentTypeAvailabilities.find((a) => a.uuid === uuid);
        if (currentAvailability) {
          currentAvailability.availabilities = availability;
        } else {
          this.availabilities[type].push({
            uuid,
            availabilities: availability,
          });
        }
      }
    });
    if (versionUuid) {
      switch (type) {
        case 'class':
          await this.updateCardClassAvailability(uuid, availability, versionUuid);
          break;
        case 'subject':
          await this.updateCardSubjectAvailability(uuid, availability, versionUuid);
          break;
        case 'room':
          await this.updateCardRoomAvailability(uuid, availability, versionUuid);
          break;
        case 'teacher':
          await this.updateCardTeacherAvailability(uuid, availability, versionUuid);
          break;
      }
    }
  }

  private async updateCardClassAvailability(
    classUuid: string,
    availability: TimetableRelationshipProperties,
    versionUuid: string,
  ) {
    const versionsContext = getCacheTag('TIMETABLE_VERSIONS');
    await urqlClient
      .mutation<
        UpdateTimetableVersionClassAvailabilityMutation,
        UpdateTimetableVersionClassAvailabilityMutationVariables
      >(
        UpdateTimetableVersionClassAvailabilityDocument,
        {
          data: {
            mon: availability.mon,
            tue: availability.tue,
            wed: availability.wed,
            thu: availability.thu,
            fri: availability.fri,
            sat: availability.sat,
            sun: availability.sun,
          },
          versionUuid: versionUuid,
          classUuid: classUuid,
        },
        versionsContext,
      )
      .toPromise();
  }

  private async updateCardTeacherAvailability(
    teacherUuid: string,
    availability: TimetableRelationshipProperties,
    versionUuid: string,
  ) {
    const versionsContext = getCacheTag('TIMETABLE_VERSIONS');
    await urqlClient
      .mutation<
        UpdateTimetableVersionTeachersAvailabilityMutation,
        UpdateTimetableVersionTeachersAvailabilityMutationVariables
      >(
        UpdateTimetableVersionTeachersAvailabilityDocument,
        {
          data: {
            mon: availability.mon,
            tue: availability.tue,
            wed: availability.wed,
            thu: availability.thu,
            fri: availability.fri,
            sat: availability.sat,
            sun: availability.sun,
          },
          versionUuid: versionUuid,
          uuid: teacherUuid,
        },
        versionsContext,
      )
      .toPromise();
  }

  private async updateCardRoomAvailability(
    roomUuid: string,
    availability: TimetableRelationshipProperties,
    versionUuid: string,
  ) {
    const versionsContext = getCacheTag('TIMETABLE_VERSIONS');
    await urqlClient
      .mutation<
        UpdateTimetableVersionRoomAvailabilityMutation,
        UpdateTimetableVersionRoomAvailabilityMutationVariables
      >(
        UpdateTimetableVersionRoomAvailabilityDocument,
        {
          data: {
            mon: availability.mon,
            tue: availability.tue,
            wed: availability.wed,
            thu: availability.thu,
            fri: availability.fri,
            sat: availability.sat,
            sun: availability.sun,
          },
          versionUuid,
          roomUuid,
        },
        versionsContext,
      )
      .toPromise();
  }

  private async updateCardSubjectAvailability(
    subjectUuid: string,
    availability: TimetableRelationshipProperties,
    versionUuid: string,
  ) {
    const versionsContext = getCacheTag('TIMETABLE_VERSIONS');
    await urqlClient
      .mutation<
        UpdateTimetableVersionSubjectAvailabilityMutation,
        UpdateTimetableVersionSubjectAvailabilityMutationVariables
      >(
        UpdateTimetableVersionSubjectAvailabilityDocument,
        {
          data: {
            mon: availability.mon,
            tue: availability.tue,
            wed: availability.wed,
            thu: availability.thu,
            fri: availability.fri,
            sat: availability.sat,
            sun: availability.sun,
          },
          versionUuid,
          subjectUuid,
        },
        versionsContext,
      )
      .toPromise();
  }

  public getTeacherAvailability(teacherUuid: string) {
    return this.availabilities?.teacher.find((a) => a.uuid === teacherUuid)?.availabilities ?? initialMatrix;
  }

  public getSubjectAvailability(subjectUuid: string) {
    return this.availabilities?.subject.find((a) => a.uuid === subjectUuid)?.availabilities ?? initialMatrix;
  }

  public getRoomAvailability(roomUuid: string) {
    return this.availabilities?.room.find((a) => a.uuid === roomUuid)?.availabilities ?? initialMatrix;
  }

  public getClassAvailability(classUuid: string) {
    return this.availabilities?.class.find((a) => a.uuid === classUuid)?.availabilities ?? initialMatrix;
  }
}
